import React, { useState, useMemo } from 'react';
import { Copy, AlertCircle, CheckCircle } from 'lucide-react';

const RegexTester = () => {
  const [text, setText] = useState('The quick brown fox jumps over the lazy dog.\nEmail: test@example.com\nPhone: 555-123-4567');
  const [pattern, setPattern] = useState('\\b\\w+@\\w+\\.\\w+\\b');
  const [flags, setFlags] = useState('g');
  const [copied, setCopied] = useState('');

  const { matches, error, highlighted } = useMemo(() => {
    if (!pattern) return { matches: [], error: null, highlighted: text };
    
    try {
      const regex = new RegExp(pattern, flags);
      const foundMatches = [...text.matchAll(regex)];
      
      let highlightedText = text;
      if (foundMatches.length > 0) {
        const parts = [];
        let lastIndex = 0;
        
        foundMatches.forEach((match) => {
          parts.push(text.substring(lastIndex, match.index));
          parts.push(`<mark>${match[0]}</mark>`);
          lastIndex = match.index + match[0].length;
        });
        parts.push(text.substring(lastIndex));
        highlightedText = parts.join('');
      }
      
      return { matches: foundMatches, error: null, highlighted: highlightedText };
    } catch (err) {
      return { matches: [], error: err.message, highlighted: text };
    }
  }, [text, pattern, flags]);

  const getRegexExplanation = (pat) => {
    const explanations = {
      '\\b\\w+@\\w+\\.\\w+\\b': 'Email address matcher - matches word characters followed by @ and domain',
      '\\d{3}-\\d{3}-\\d{4}': 'US phone number - matches XXX-XXX-XXXX format',
      '\\b[A-Z][a-z]+\\b': 'Capitalized words - matches words starting with uppercase',
      '^\\s*$': 'Empty lines - matches lines with only whitespace',
      '\\bhttps?://\\S+': 'URLs - matches http or https links',
      '\\b(?:\\d{1,3}\\.){3}\\d{1,3}\\b': 'IP addresses - matches IPv4 format',
    };
    return explanations[pat] || 'Enter a regex pattern to test';
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(''), 2000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6 font-mono">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;600;700&family=Inter:wght@500;600;700&display=swap');
        
        * {
          font-family: 'JetBrains Mono', monospace;
        }
        
        h1, h2 {
          font-family: 'Inter', sans-serif;
          letter-spacing: -0.02em;
        }

        mark {
          background: linear-gradient(120deg, #3b82f6, #1d4ed8);
          color: #fff;
          padding: 2px 4px;
          border-radius: 3px;
          font-weight: 600;
        }

        input, textarea {
          background: rgba(30, 41, 59, 0.8);
          border: 1px solid rgba(71, 85, 105, 0.5);
          transition: all 0.2s ease;
        }

        input:focus, textarea:focus {
          background: rgba(15, 23, 42, 0.9);
          border-color: rgba(59, 130, 246, 0.8);
          box-shadow: 0 0 20px rgba(59, 130, 246, 0.2);
          outline: none;
        }

        button {
          transition: all 0.2s ease;
        }

        button:hover {
          transform: translateY(-1px);
        }

        button:active {
          transform: translateY(0);
        }

        .fade-in {
          animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .match-count {
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateX(-10px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>

      <div className="max-w-6xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Regex Tester</h1>
          <p className="text-slate-400 text-sm">Test and debug regular expressions in real-time</p>
        </div>

        <div className="grid grid-cols-3 gap-6 mb-6">
          <div className="col-span-1">
            <label className="block text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Input Text</label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full h-64 p-4 rounded-lg text-slate-100 placeholder-slate-500 resize-none text-sm leading-relaxed focus:ring-0"
              placeholder="Enter text to test..."
            />
          </div>

          <div className="col-span-1">
            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Pattern</label>
              <input
                type="text"
                value={pattern}
                onChange={(e) => setPattern(e.target.value)}
                className="w-full px-4 py-2 rounded-lg text-slate-100 placeholder-slate-500 text-sm focus:ring-0"
                placeholder="Enter regex pattern..."
              />
            </div>

            <div className="mb-4">
              <label className="block text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Flags</label>
              <div className="flex gap-2">
                {['g', 'i', 'm', 's'].map((flag) => (
                  <button
                    key={flag}
                    onClick={() => {
                      if (flags.includes(flag)) {
                        setFlags(flags.replace(flag, ''));
                      } else {
                        setFlags(flags + flag);
                      }
                    }}
                    className={`px-3 py-1 rounded text-xs font-semibold uppercase tracking-wide transition-all ${
                      flags.includes(flag)
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                    }`}
                    title={
                      flag === 'g' ? 'Global' :
                      flag === 'i' ? 'Case insensitive' :
                      flag === 'm' ? 'Multiline' :
                      'Single line'
                    }
                  >
                    {flag}
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600/50">
              <p className="text-xs text-slate-300">{getRegexExplanation(pattern)}</p>
            </div>
          </div>

          <div className="col-span-1">
            <label className="block text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Results</label>
            
            {error ? (
              <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-600/50 rounded-lg fade-in">
                <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-red-300 text-xs font-semibold">Invalid Pattern</p>
                  <p className="text-red-200/70 text-xs mt-1">{error}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-slate-700/30 rounded-lg p-3 border border-slate-600/50">
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-wide">Matches Found</p>
                    <p className="text-2xl font-bold text-blue-400 match-count">{matches.length}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500/60" />
                </div>

                {matches.length > 0 && (
                  <div className="bg-slate-700/30 rounded-lg p-3 border border-slate-600/50 max-h-40 overflow-y-auto">
                    <p className="text-xs text-slate-400 uppercase tracking-wide mb-2">Captured Matches</p>
                    <div className="space-y-1">
                      {matches.slice(0, 10).map((match, idx) => (
                        <div key={idx} className="text-xs text-slate-200 bg-slate-800/50 px-2 py-1 rounded flex items-center justify-between group">
                          <span className="font-mono break-all">{match[0]}</span>
                          <button
                            onClick={() => copyToClipboard(match[0], `match-${idx}`)}
                            className="opacity-0 group-hover:opacity-100 ml-2 flex-shrink-0"
                            title="Copy match"
                          >
                            {copied === `match-${idx}` ? (
                              <span className="text-green-400 text-xs">✓</span>
                            ) : (
                              <Copy className="w-3 h-3 text-slate-400" />
                            )}
                          </button>
                        </div>
                      ))}
                      {matches.length > 10 && (
                        <p className="text-xs text-slate-500 italic">... and {matches.length - 10} more</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bg-slate-700/20 border border-slate-600/50 rounded-lg p-4">
          <p className="text-xs font-semibold text-slate-300 mb-2 uppercase tracking-wide">Preview</p>
          <div 
            className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap break-words font-mono"
            dangerouslySetInnerHTML={{ __html: highlighted }}
          />
        </div>

        <div className="mt-8 pt-6 border-t border-slate-700/50 text-center text-xs text-slate-500">
          <p>Regex Tester • Built for developers • <span className="text-slate-400">v1.0</span></p>
        </div>
      </div>
    </div>
  );
};

export default RegexTester;